import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, MenuItem, Paper, TextField, Typography,
} from '@mui/material';
import { SalesSettingsService } from '../../services/salesSettings.service';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import type { SalesSettings as SalesSettingsData } from '../../interfaces/sales/salesSettings.interface';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { leafAccounts } from '../../utils/postingAccounts';
import { useToast } from '../../context/ToastContext';
// ---- FW-2B cash sale defaults ----
import { CashSaleService } from '../../services/cashSale.service';
import { CustomerService } from '../../services/customer.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { CashSaleSettings } from '../../interfaces/sales/cashSale.interface';
import { ReceiptMethod } from '../../interfaces/sales/receipt.interface';
import DiscountLimitsCard from './DiscountLimitsCard';   // S1
import SearchableSelect from '../common/SearchableSelect';
import { cashSaleDepositOptions } from '../../utils/postingAccounts';
// ---- end FW-2B ----

const EMPTY_FORM: SalesSettingsData = {
  institutionId: '',
  arAccountId: '',
  defaultRevenueAccountId: '',
  taxPayableAccountId: '',
  defaultTaxRatePct: 0,
  defaultQuotationValidityDays: 30,
  defaultPaymentTermsDays: 30,
};

const SalesSettings: React.FC = () => {
  const toast = useToast();
  const [form, setForm] = useState<SalesSettingsData>(EMPTY_FORM);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // ---- FW-2B cash sale defaults (saved apart from the general settings: they never overwrite each other) ----
  const [cash, setCash] = useState<CashSaleSettings>({ institutionId: '' });
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [savingCash, setSavingCash] = useState(false);
  // ---- end FW-2B ----

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    const institutionId = AuthService.getSelectedInstitution()!!;
    try {
      setLoading(true);
      const [settings, accountsRes] = await Promise.all([
        SalesSettingsService.getByInstitution(institutionId).catch(() => null),
        AccountService.getAllAccounts(),
      ]);
      setAccounts(accountsRes ?? []);
      // ---- FW-2B ----
      CashSaleService.getSettings(institutionId).then(setCash).catch(() => setCash({ institutionId }));
      CustomerService.listByInstitution(institutionId, 0, 200).then(p => setCustomers(p.content)).catch(() => setCustomers([]));
      // ---- end FW-2B ----
      if (settings) {
        setForm({
          institutionId: settings.institutionId ?? institutionId,
          arAccountId: settings.arAccountId ?? '',
          defaultRevenueAccountId: settings.defaultRevenueAccountId ?? '',
          taxPayableAccountId: settings.taxPayableAccountId ?? '',
          defaultTaxRatePct: settings.defaultTaxRatePct ?? 0,
          defaultQuotationValidityDays: settings.defaultQuotationValidityDays ?? 30,
          defaultPaymentTermsDays: settings.defaultPaymentTermsDays ?? 30,
        });
      }
    } catch (e) {
      toast.showApiError(e, 'Failed to load sales settings');
    } finally {
      setLoading(false);
    }
  };

  const setAccount = (k: keyof SalesSettingsData) =>
    (id: string) => setForm({ ...form, [k]: id });

  const handleNumberField = (k: keyof SalesSettingsData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const n = parseFloat(e.target.value);
      setForm({ ...form, [k]: Number.isFinite(n) ? n : 0 });
    };

  const handleSave = async () => {
    const institutionId = AuthService.getSelectedInstitution()!!;
    try {
      setSaving(true);
      await SalesSettingsService.upsert({ ...form, institutionId });
      toast.showSuccess('Sales settings saved');
    } catch (e) {
      toast.showApiError(e, 'Failed to save sales settings');
    } finally {
      setSaving(false);
    }
  };

  // ---- FW-2B ----
  const saveCash = async () => {
    const institutionId = AuthService.getSelectedInstitution()!!;
    try {
      setSavingCash(true);
      setCash(await CashSaleService.saveSettings({ ...cash, institutionId }));
      toast.showSuccess('Cash sale settings saved');
    } catch (e) {
      toast.showApiError(e, 'Failed to save cash sale settings');
    } finally {
      setSavingCash(false);
    }
  };

  const createWalkIn = async () => {
    const institutionId = AuthService.getSelectedInstitution()!!;
    try {
      const updated = await CashSaleService.createWalkInCustomer(institutionId);
      setCash(updated);
      setCustomers((await CustomerService.listByInstitution(institutionId, 0, 200)).content);
      toast.showSuccess('Walk-in customer is ready');
    } catch (e) {
      toast.showApiError(e, 'Failed to create the walk-in customer');
    }
  };
  // ---- end FW-2B ----

  // Group (header) accounts never receive postings, so only leaf accounts are offered.
  const postingAccounts = leafAccounts(accounts);
  const assetAccounts = postingAccounts.filter(a => a.accountType === 'ASSET');
  const revenueAccounts = postingAccounts.filter(a => a.accountType === 'REVENUE');

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 720 }} data-help-page="sales.settingsForm.page">
      <Typography variant="h5" sx={{ mb: 2 }}>Sales Settings</Typography>
      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="sales.settingsForm.section.accounts">

        <AccountAutocomplete
          data-help="sales.settingsForm.field.arAccount"
          label="AR Account"
          accounts={assetAccounts}
          value={form.arAccountId ?? ''}
          onChange={setAccount('arAccountId')}
          placeholder="None"
        />

        <AccountAutocomplete
          data-help="sales.settingsForm.field.revenueAccount"
          label="Default Revenue Account"
          accounts={revenueAccounts}
          value={form.defaultRevenueAccountId ?? ''}
          onChange={setAccount('defaultRevenueAccountId')}
          placeholder="None"
        />

        <AccountAutocomplete
          data-help="sales.settingsForm.field.taxPayableAccount"
          label="Tax Payable Account"
          accounts={postingAccounts}
          value={form.taxPayableAccountId ?? ''}
          onChange={setAccount('taxPayableAccountId')}
          placeholder="None"
        />

        <TextField
          data-help="sales.settingsForm.field.taxRate"
          label="Default Tax Rate (%)"
          type="number"
          value={form.defaultTaxRatePct ?? 0}
          onChange={handleNumberField('defaultTaxRatePct')}
          inputProps={{ min: 0, max: 100, step: 0.01, 'aria-label': 'Default Tax Rate' }}
          fullWidth
        />

        <TextField
          data-help="sales.settingsForm.field.quotationValidity"
          label="Default Quotation Validity (days)"
          type="number"
          value={form.defaultQuotationValidityDays ?? 30}
          onChange={handleNumberField('defaultQuotationValidityDays')}
          inputProps={{ min: 0, step: 1, 'aria-label': 'Default Quotation Validity Days' }}
          fullWidth
        />

        <TextField
          data-help="sales.settingsForm.field.paymentTerms"
          label="Default Payment Terms (days)"
          type="number"
          value={form.defaultPaymentTermsDays ?? 30}
          onChange={handleNumberField('defaultPaymentTermsDays')}
          inputProps={{ min: 0, step: 1, 'aria-label': 'Default Payment Terms Days' }}
          fullWidth
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button data-help="sales.settingsForm.save" variant="contained" onClick={handleSave} disabled={saving}>Save</Button>
        </Box>
      </Paper>

      {/* ---- FW-2B: cash sale (sales receipt) defaults ---- */}
      <Paper sx={{ p: 3, mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="sales.settingsForm.section.cash">
        <Typography variant="h6">Cash sale</Typography>
        <Typography variant="body2" color="text.secondary">
          A cash sale issues the invoice and receives the payment in one step. Sales with no named customer go to the walk-in customer.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 260px', maxWidth: 420 }}>
            <SearchableSelect
              data-help="sales.settingsForm.cash.walkIn"
              label="Walk-in customer"
              options={customers}
              value={cash.cashCustomerId ?? ''}
              onChange={id => setCash({ ...cash, cashCustomerId: id || null })}
              getOptionLabel={c => c.name}
              clearable
            />
          </Box>
          <Button data-help="sales.settingsForm.cash.createWalkIn" variant="outlined" onClick={createWalkIn} disabled={!!cash.cashCustomerId}>Create walk-in customer</Button>
        </Box>
        <AccountAutocomplete
          data-help="sales.settingsForm.cash.depositAccount"
          label="Default deposit account"
          accounts={cashSaleDepositOptions(assetAccounts)}
          value={cash.depositAccountId ?? ''}
          onChange={id => setCash({ ...cash, depositAccountId: id || null })}
          placeholder="None"
        />
        <TextField
          data-help="sales.settingsForm.cash.method"
          select label="Default method" value={cash.receiptMethod ?? ''}
          onChange={e => setCash({ ...cash, receiptMethod: (e.target.value || null) as ReceiptMethod | null })}
          sx={{ flex: '1 1 200px', maxWidth: 280 }}
        >
          <MenuItem value="">None (cash)</MenuItem>
          <MenuItem value="CASH">Cash</MenuItem>
          <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
          <MenuItem value="MOBILE_BANKING">Mobile Banking</MenuItem>
          <MenuItem value="OTHER">Other</MenuItem>
        </TextField>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button data-help="sales.settingsForm.cash.save" variant="contained" onClick={saveCash} disabled={savingCash}>Save cash sale settings</Button>
        </Box>
      </Paper>
      {/* ---- end FW-2B ---- */}
      <DiscountLimitsCard />   {/* S1 */}
    </Box>
  );
};

export default SalesSettings;
